import React, {ReactNode, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callAsyncWithDataModifications} from 'audit/DataModificationsModal';
import * as Context from 'Context';
import {ActionOrigin, StudyPhase, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getProceedTextForStudyPhase, getReturnTextForStudyPhase} from 'studies/StudyPhaseUtils';
import PhaseChangeModal from 'studies/typed/shared/PhaseChangeModal';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import {assertNever} from 'util/Types';

import * as StudyDAO from '../../StudyDAO';

/**
 * Message to display when changing study phase.
 * DESIGN <-> EXECUTION <-> COMPLETE <-> DESIGN ...
 * The backwards message for COMPLETE is never used.
 */
export interface PhaseChangeMessage {
  forwardsMessage?: ReactNode;
  backwardsMessage?: ReactNode;
}

export interface SetStudyPhaseDropdownProps {
  study: {id: string; phase: StudyPhase; studyType: StudyType};
  refetch: () => Promise<unknown>;
  isDisabled: boolean;
  requiresApproval: boolean;
  allowReturnToDesignPhase: boolean;
  actionOrigin?: ActionOrigin;
  buttonSize?: ButtonSize;
  confirmPhaseChange?: Record<StudyPhase, PhaseChangeMessage | null>;
  proceedToExecutionLogger?: (studyId: string, studyType: StudyType) => (actionOrigin: ActionOrigin) => void;
}

export async function updateStudyPhaseWithDataMods(
  studyId: string,
  phase: StudyPhase,
  actionOrigin: ActionOrigin,
  refetch: () => Promise<unknown>
): Promise<void> {
  await callAsyncWithDataModifications({
    changeSummary: 'The study phase will be updated.',
    onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
      await StudyDAO.updateStudy(studyId, {phase}, dataModsHeader);
      await refetch();
    },
    apiIds: [studyId],
    analyticsParams: {
      module: 'STUDIES',
      actionOrigin,
      modifiedAction: 'STUDY_PHASE_UPDATE',
      numModifiedTotalItems: 1,
      entryId: null,
    },
    useProjectLevelDataModsLogic: true,
  });
}

export default function SetStudyPhaseDropdown({
  study,
  refetch,
  isDisabled,
  requiresApproval,
  allowReturnToDesignPhase,
  buttonSize,
  confirmPhaseChange,
  actionOrigin,
  proceedToExecutionLogger,
}: SetStudyPhaseDropdownProps): JSX.Element | null {
  const [loading, setLoading] = useState(false);
  const actionOriginValue = actionOrigin ?? ActionOrigin.STUDY_OVERVIEW_PAGE;

  const setPhase = async (
    phase: StudyPhase,
    setPhaseLogger?: (studyId: string, studyType: StudyType) => (actionOrigin: ActionOrigin) => void
  ): Promise<void> => {
    const setPhaseEndLogger = setPhaseLogger?.(study.id, study.studyType);
    setLoading(true);
    await Context.usingAsync(
      {
        analyticsMetadata: {
          actionOriginValue,
        },
      },
      null,
      async () => {
        try {
          if (
            (study.studyType === StudyType.CLINICAL || study.studyType === StudyType.PRECLINICAL) &&
            phase === StudyPhase.DESIGN
          ) {
            await updateStudyPhaseWithDataMods(study.id, phase, actionOriginValue, refetch);
          } else {
            await StudyDAO.updateStudy(study.id, {phase});
            await refetch();
          }
        } finally {
          setLoading(false);
        }
      }
    );
    setPhaseEndLogger?.(actionOriginValue);
  };

  const getPhaseChangeMessage = (phase: StudyPhase, isForwards: boolean): ReactNode | undefined => {
    // Can't go backwards from DESIGN to COMPLETE
    if (phase === StudyPhase.COMPLETE && !isForwards) {
      return null;
    }
    if (!confirmPhaseChange) {
      return null;
    }
    const phaseChange = confirmPhaseChange[phase];
    return isForwards ? phaseChange?.forwardsMessage : phaseChange?.backwardsMessage;
  };

  const handleClick = async (
    phase: StudyPhase,
    isForwards: boolean,
    setPhaseLogger?: (studyId: string, studyType: StudyType) => (actionOrigin: ActionOrigin) => void
  ): Promise<void> => {
    const phaseChangeMessage = getPhaseChangeMessage(phase, isForwards);
    if (phaseChangeMessage) {
      const confirmCancel = await legacyRunModal(PhaseChangeModal, {
        currentStudyPhase: study.phase,
        nextStudyPhase: phase,
        phaseMessage: phaseChangeMessage,
      });
      if (confirmCancel == null) {
        return;
      }
    }
    setPhase(phase, setPhaseLogger);
  };

  return (
    <div
      {...getTestProps({
        component: 'SetStudyPhaseDropdown',
        element: 'root',
      })}
    >
      {(() => {
        switch (study.phase) {
          case StudyPhase.DESIGN:
            return (
              <Tooltip
                disabled={!requiresApproval}
                title="Study design must be approved before proceeding to execution"
              >
                <Button
                  disabled={loading || isDisabled || requiresApproval}
                  onClick={() => {
                    handleClick(StudyPhase.EXECUTION, /* isForwards */ true, proceedToExecutionLogger);
                  }}
                  size={buttonSize ?? ButtonSize.SMALL}
                  buttonStyle={ButtonStyle.PRIMARY}
                  text={getProceedTextForStudyPhase(StudyPhase.EXECUTION)}
                />
              </Tooltip>
            );
          case StudyPhase.EXECUTION:
            return !allowReturnToDesignPhase ? (
              <Button
                disabled={loading || isDisabled || requiresApproval}
                onClick={() => {
                  handleClick(StudyPhase.COMPLETE, /* isForwards */ true);
                }}
                size={buttonSize ?? ButtonSize.SMALL}
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                text="Mark study as complete"
              />
            ) : (
              <Dropdown
                menu={
                  <Menu width={Menu.WIDTHS.MEDIUM}>
                    <Section>
                      <Option
                        onSelect={() => {
                          handleClick(StudyPhase.COMPLETE, /* isForwards */ true);
                        }}
                      >
                        Mark study as complete
                      </Option>
                    </Section>
                    <Section>
                      <Option
                        onSelect={() => {
                          handleClick(StudyPhase.DESIGN, /* isForwards */ false);
                        }}
                      >
                        {getReturnTextForStudyPhase(StudyPhase.DESIGN)}
                      </Option>
                    </Section>
                  </Menu>
                }
              >
                <Button
                  disabled={loading || isDisabled}
                  isDropdown={true}
                  size={buttonSize ?? ButtonSize.SMALL}
                  buttonStyle={ButtonStyle.PRIMARY}
                  text={getProceedTextForStudyPhase(StudyPhase.COMPLETE)}
                  autoHeight={true}
                />
              </Dropdown>
            );
          case StudyPhase.COMPLETE:
            return (
              <Dropdown
                menu={
                  <Menu width={Menu.WIDTHS.FIT_CONTENT}>
                    {allowReturnToDesignPhase && (
                      <Option
                        onSelect={() => {
                          handleClick(StudyPhase.DESIGN, /* isForwards */ true);
                        }}
                      >
                        {getReturnTextForStudyPhase(StudyPhase.DESIGN)}
                      </Option>
                    )}
                    <Option
                      onSelect={() => {
                        handleClick(StudyPhase.EXECUTION, /* isForwards */ false);
                      }}
                    >
                      {getReturnTextForStudyPhase(StudyPhase.EXECUTION)}
                    </Option>
                  </Menu>
                }
              >
                <Button
                  disabled={loading || isDisabled}
                  isDropdown={true}
                  size={buttonSize ?? ButtonSize.SMALL}
                  buttonStyle={ButtonStyle.TERTIARY_BLUE}
                  text="Reopen study"
                />
              </Dropdown>
            );
          default:
            assertAndContinue(false, `Unexpected phase: ${assertNever(study.phase)}`);
            return null;
        }
      })()}
    </div>
  );
}
